import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { buildScheduleResolutionQueue } from "@common/helpers/domains/accounting/scheduleMatchConflicts";
import {
    MatchScheduleConflict,
    ScheduleMatchResolution,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ScheduleMatchResolutionKind } from "@common/types/domains/accounting/schedule";
import { ScheduleConflictWizardModal } from "./ScheduleConflictWizardModal";
import { buildScheduleConflictStepView } from "./scheduleConflictWizardCopy";

export type ScheduleConflictWizardOutcome =
    | { kind: "resolved"; resolutions: ScheduleMatchResolution[] }
    /** Back to the surface with its selection intact; nothing is saved. */
    | { kind: "back" }
    /** The whole save is abandoned. */
    | { kind: "cancel" };

interface WizardState {
    conflicts: MatchScheduleConflict[];
    resolutions: ScheduleMatchResolution[];
    isSinglePair: boolean;
}

/**
 * Walks the user through every invoice still in conflict, one step each,
 * recomputing after every answer (keeping an invoice's schedule can clear the
 * next invoice too when they share a transaction). Rendered as a local node
 * because the surfaces that open it are overlays themselves.
 */
export function useScheduleConflictWizard(): {
    runScheduleConflictWizard: (
        conflicts: MatchScheduleConflict[],
    ) => Promise<ScheduleConflictWizardOutcome>;
    scheduleConflictWizard: ReactNode;
} {
    const [state, setState] = useState<WizardState | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const pendingResolveRef = useRef<
        ((outcome: ScheduleConflictWizardOutcome) => void) | null
    >(null);

    const runScheduleConflictWizard = useCallback(
        (conflicts: MatchScheduleConflict[]) =>
            new Promise<ScheduleConflictWizardOutcome>((resolve) => {
                const queue = buildScheduleResolutionQueue({
                    conflicts,
                    resolutions: [],
                });
                if (queue.length === 0) {
                    resolve({ kind: "resolved", resolutions: [] });
                    return;
                }
                pendingResolveRef.current?.({ kind: "cancel" });
                pendingResolveRef.current = resolve;
                setState({
                    conflicts,
                    resolutions: [],
                    isSinglePair:
                        queue.length === 1 &&
                        queue[0].transactions.length === 1,
                });
                setIsOpen(true);
            }),
        [],
    );

    const queue = useMemo(
        () =>
            state
                ? buildScheduleResolutionQueue({
                      conflicts: state.conflicts,
                      resolutions: state.resolutions,
                  })
                : [],
        [state],
    );
    const current = queue[0];

    const finish = useCallback((outcome: ScheduleConflictWizardOutcome) => {
        pendingResolveRef.current?.(outcome);
        pendingResolveRef.current = null;
        setIsOpen(false);
    }, []);

    const confirmStep = useCallback(
        async (kind: ScheduleMatchResolutionKind) => {
            if (!state || !current) {
                return;
            }
            const resolutions = [
                ...state.resolutions,
                {
                    invoiceId: current.invoiceId,
                    resolution: kind,
                    transactionIds: current.transactions.map(
                        (transaction) => transaction.transactionId,
                    ),
                },
            ];
            const remaining = buildScheduleResolutionQueue({
                conflicts: state.conflicts,
                resolutions,
            });
            if (remaining.length === 0) {
                finish({ kind: "resolved", resolutions });
                return;
            }
            setState({ ...state, resolutions });
        },
        [current, finish, state],
    );

    const scheduleConflictWizard =
        state && current ? (
            <ScheduleConflictWizardModal
                key={current.invoiceId}
                show={isOpen}
                view={buildScheduleConflictStepView({
                    step: current,
                    stepIndex: state.resolutions.length,
                    stepCount: state.resolutions.length + queue.length,
                    isSinglePair: state.isSinglePair,
                })}
                onConfirm={confirmStep}
                onBack={() => finish({ kind: "back" })}
                onClose={() => finish({ kind: "cancel" })}
                onMatchOneAtATime={() => finish({ kind: "cancel" })}
                onExited={() => setState(null)}
            />
        ) : null;

    return { runScheduleConflictWizard, scheduleConflictWizard };
}
